import React from 'react';

// ─── Buttons ────────────────────────────────────────────────────────
export const Btn = ({ variant = 'primary', children, onClick, disabled, full, small, type = 'button', className = '' }) => {
    const cls = [
        'okbtn',
        `okbtn-${variant}`,
        full ? 'okbtn-full' : '',
        small ? 'okbtn-sm' : '',
        className,
    ].filter(Boolean).join(' ');
    return (
        <button type={type} className={cls} onClick={onClick} disabled={disabled}>
            {children}
        </button>
    );
};

// ─── Labels & numerics ───────────────────────────────────────────────
export const Label = ({ children, color = 'muted', className = '' }) => (
    <span className={`oklbl oklbl-${color} ${className}`}>{children}</span>
);

export const Num = ({ children, color, size = 'md', glow }) => {
    const style = {};
    if (color) style.color = color;
    if (glow) style.textShadow = `0 0 12px ${glow}`;
    return <span className={`oknum oknum-${size}`} style={style}>{children}</span>;
};

// ─── Rule (gold hairline divider) ───────────────────────────────────
export const Rule = ({ thin, className = '' }) => (
    <div className={`okrule ${thin ? 'okrule-thin' : ''} ${className}`} />
);

// ─── Inline SVG icons (Lucide paths, React-rendered) ────────────────
// Previously this used `<i data-lucide=...>` + window.lucide.createIcons(),
// which REPLACES the <i> with an <svg> via direct DOM mutation. React 18
// then crashed on unmount with "removeChild: not a child of this node"
// during the CommandBox MOVE → FIGHT transition (chevron icons unmounting).
// SVGs rendered by React stay in React's tree — no external mutation.
const ICON_PATHS = {
    'chevron-up':    'm18 15-6-6-6 6',
    'chevron-down':  'm6 9 6 6 6-6',
    'chevron-left':  'm15 18-6-6 6-6',
    'chevron-right': 'm9 18 6-6-6-6',
};

// Backpack uses multiple paths.
const BACKPACK_PATHS = [
    'M4 10a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z',
    'M8 10h8',
    'M8 18v-4a4 4 0 0 1 8 0v4',
    'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2',
];

export const Icon = ({ name, size = 22, color, strokeWidth = 1.5, className = '' }) => {
    const common = {
        width: size,
        height: size,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: color || 'currentColor',
        strokeWidth,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        className,
        style: { display: 'inline-block', verticalAlign: 'middle' },
    };
    if (name === 'backpack') {
        return (
            <svg {...common} xmlns="http://www.w3.org/2000/svg">
                {BACKPACK_PATHS.map((d, i) => <path key={i} d={d} />)}
            </svg>
        );
    }
    const d = ICON_PATHS[name];
    if (!d) return null;
    return (
        <svg {...common} xmlns="http://www.w3.org/2000/svg">
            <path d={d} />
        </svg>
    );
};

// ─── Gilt disc — round badge for class glyphs ───────────────────────
export const GiltDisc = ({ children, size = 56, glow, className = '' }) => (
    <div
        className={`okdisc ${className}`}
        style={{
            width: size,
            height: size,
            boxShadow: glow ? '0 0 0 1px var(--okoyl-300), 0 0 24px rgba(200,144,42,0.5)' : undefined,
        }}
    >
        {children}
    </div>
);

// ─── StatBar — HP / MP / XP ─────────────────────────────────────────
export const StatBar = ({ label, value, max, kind = 'hp', extra }) => {
    const safeMax = Math.max(1, Number(max) || 1);
    const safeVal = Math.max(0, Number(value) || 0);
    const pct = Math.min(100, (safeVal / safeMax) * 100);
    const low = kind === 'hp' && pct < 25;
    return (
        <div className="okbar">
            <div className="okbar-row">
                <span>{label}</span>
                <span>
                    <Num size="sm" color={low ? 'var(--crit)' : undefined}>{safeVal} / {safeMax}</Num>
                    {extra && <span className="okbar-extra">{extra}</span>}
                </span>
            </div>
            <div className="okbar-track">
                <div
                    className={`okbar-fill okbar-${kind}${low ? ' okbar-low' : ''}`}
                    style={{ width: pct + '%' }}
                />
            </div>
        </div>
    );
};

// ─── Portrait cartouche (hero / monster / NPC framed in gilt) ──────
export const Portrait = ({ src, name, sub, selected, size = 'md', tint = 'gold', onClick, className = '' }) => {
    const cls = [
        'okport',
        `okport-${size}`,
        `okport-${tint}`,
        selected ? 'okport-on' : '',
        onClick ? 'okport-tap' : '',
        className,
    ].filter(Boolean).join(' ');
    return (
        <div className={cls} onClick={onClick}>
            <div className="okport-img" style={{ backgroundImage: `url(${src})` }} />
            {(name || sub) && (
                <div className="okport-cap">
                    {name && <div className="okport-name">{name}</div>}
                    {sub && <div className="okport-sub">{sub}</div>}
                </div>
            )}
        </div>
    );
};

// ─── Card ───────────────────────────────────────────────────────────
export const Card = ({ title, glyph, children, selected, corrupted, onClick, padded = true, className = '' }) => {
    const cls = [
        'okcard',
        selected ? 'okcard-on' : '',
        corrupted ? 'okcard-cor' : '',
        onClick ? 'okcard-tap' : '',
        !padded ? 'okcard-bare' : '',
        className,
    ].filter(Boolean).join(' ');
    return (
        <div className={cls} onClick={onClick}>
            {glyph && <span className="okcard-glyph">{glyph}</span>}
            {title && <div className="okcard-title">{title}</div>}
            {title && <Rule />}
            {children}
        </div>
    );
};

// ─── ScreenFrame ────────────────────────────────────────────────────
// Wraps a screen with the parchment-on-ash background, optional brand mark,
// optional title block. `bg`: "page" | "vault".
export const ScreenFrame = ({ children, title, subtitle, brand = true, bg = 'page', label, brandSrc = '/assets/okoyl-logo.png', className = '' }) => (
    <div className={`okframe okframe-${bg} ${className}`} data-screen-label={label}>
        {brand && (
            <div className="okframe-brand">
                <img src={brandSrc} alt="Okoyl" />
            </div>
        )}
        {title && (
            <div className="okframe-head">
                <div className="okframe-title">{title}</div>
                {subtitle && <div className="okframe-sub">{subtitle}</div>}
                <div className="okframe-rule" />
            </div>
        )}
        <div className="okframe-body">{children}</div>
    </div>
);

// ─── Coin glyph (gauld currency) ────────────────────────────────────
export const Coin = ({ className = '' }) => <span className={`okcoin ${className}`} />;
